import { FAST_API_URL, postOptions } from "../index";

interface ForceAlignFragmentsParams {
    mediaId: string;
    folderNum: number;
    lang: string;
    language_from?: string;
    fragment_ids?: string[];
}

export interface ForceAlignOriginalFragmentsParams {
    mediaId: string;
    folderNum: number;
    lang: string;
    language_from: string;
    folder_from: string;
    fragment_ids?: string[];
}

interface ForceAlignFragmentsResponse {
    success: boolean;
    message?: string;
}

interface AffectedFragment {
    id: string;
    captions: string;
    current_start_ms: number;
    original_start_ms: number;
    diff_ms: number;
}

interface ForceAlignPreviewResponse {
    status: string;
    total: number;
    affected: AffectedFragment[];
}

export const forceAlignFragmentsApi = async ({
    mediaId,
    folderNum,
    lang,
    language_from,
    fragment_ids,
}: ForceAlignFragmentsParams): Promise<ForceAlignFragmentsResponse> => {
    const response = await fetch(
        `${FAST_API_URL}/force_align_fragments?media=${mediaId}`,
        {
            ...postOptions,
            body: JSON.stringify({
                folder_num: folderNum,
                lang: lang,
                language_from: language_from,
                fragment_ids: fragment_ids || null,
            }),
        }
    );

    if (!response.ok) {
        throw new Error("Failed to force align fragments");
    }

    return await response.json();
};

export const forceAlignPreviewApi = async ({
    mediaId,
    folderNum,
    lang,
    language_from,
    fragment_ids,
}: ForceAlignFragmentsParams): Promise<ForceAlignPreviewResponse> => {
    const response = await fetch(
        `${FAST_API_URL}/force_align_preview?media=${mediaId}`,
        {
            ...postOptions,
            body: JSON.stringify({
                folder_num: folderNum,
                lang: lang,
                language_from: language_from,
                fragment_ids: fragment_ids || null,
            }),
        }
    );

    if (!response.ok) {
        throw new Error("Failed to get resync preview");
    }

    return await response.json();
};

export const forceAlignOriginalFragmentsApi = async ({
    mediaId,
    folderNum,
    lang,
    language_from,
    folder_from,
    fragment_ids,
}: ForceAlignOriginalFragmentsParams): Promise<ForceAlignFragmentsResponse> => {
    const response = await fetch(
        `${FAST_API_URL}/force_align_original_fragments?media=${mediaId}`,
        {
            ...postOptions,
            body: JSON.stringify({
                folder_num: folderNum,
                lang: lang,
                language_from: language_from,
                folder_from: folder_from,
                fragment_ids: fragment_ids || null,
            }),
        }
    );

    if (!response.ok) {
        throw new Error("Failed to force align original fragments");
    }

    return await response.json();
};

export const forceAlignOriginalPreviewApi = async ({
    mediaId,
    folderNum,
    lang,
    language_from,
    folder_from,
    fragment_ids,
}: ForceAlignOriginalFragmentsParams): Promise<ForceAlignPreviewResponse> => {
    const response = await fetch(
        `${FAST_API_URL}/force_align_original_preview?media=${mediaId}`,
        {
            ...postOptions,
            body: JSON.stringify({
                folder_num: folderNum,
                lang: lang,
                language_from: language_from,
                folder_from: folder_from,
                fragment_ids: fragment_ids || null,
            }),
        }
    );

    if (!response.ok) {
        throw new Error("Failed to get original resync preview");
    }

    return await response.json();
};

interface UndoResyncParams {
    mediaId: string;
    folderNum: number;
    lang: string;
    language_from?: string;
}

interface UndoResyncResponse {
    status: string;
    restored_count: number;
    message: string;
}

export interface ResyncUndoInfoResponse {
    available: boolean;
    timestamp?: string;
    mode?: string;
    lang?: string;
    language_from?: string;
    fragment_count?: number;
    fragment_ids?: string[] | null;
}

export const undoResyncApi = async ({
    mediaId,
    folderNum,
    lang,
    language_from,
}: UndoResyncParams): Promise<UndoResyncResponse> => {
    const response = await fetch(
        `${FAST_API_URL}/undo_resync?media=${mediaId}`,
        {
            ...postOptions,
            body: JSON.stringify({
                folder_num: folderNum,
                lang: lang,
                language_from: language_from || null,
            }),
        }
    );

    if (!response.ok) {
        throw new Error("Failed to undo resync");
    }

    return await response.json();
};

export const resyncUndoInfoApi = async ({
    mediaId,
    folderNum,
    lang,
    language_from,
}: UndoResyncParams): Promise<ResyncUndoInfoResponse> => {
    const response = await fetch(
        `${FAST_API_URL}/resync_undo_info?media=${mediaId}`,
        {
            ...postOptions,
            body: JSON.stringify({
                folder_num: folderNum,
                lang: lang,
                language_from: language_from || null,
            }),
        }
    );

    if (!response.ok) {
        throw new Error("Failed to get resync undo info");
    }

    return await response.json();
};
